import type { EtiologicalAgentDto } from '@/api/WhisApi/generated/model/etiologicalAgentDto';
import type { EtiologyTypeDto } from '@/api/WhisApi/generated/model/etiologyTypeDto';
import type { FamilyDto } from '@/api/WhisApi/generated/model/familyDto';
import type { SampleTypeDto } from '@/api/WhisApi/generated/model/sampleTypeDto';
import type { SubCategoryDto } from '@/api/WhisApi/generated/model/subCategoryDto';
import type { WahisEtiologicalAgentDto } from '@/api/WhisApi/generated/model/wahisEtiologicalAgentDto';
import type { FormOption } from '@/screens/ReportCreation/reportCreation.types';
import { SupportedLanguage, supportedLanguages } from '@store/i18n';

const ENGLISH_LANGUAGE = SupportedLanguage.en;

const buildLanguagePriority = (
  language: SupportedLanguage,
): SupportedLanguage[] => {
  const remainingLanguages = supportedLanguages.filter(
    (lang) => lang !== language && lang !== ENGLISH_LANGUAGE,
  );

  return language === ENGLISH_LANGUAGE
    ? [ENGLISH_LANGUAGE, ...remainingLanguages]
    : [language, ENGLISH_LANGUAGE, ...remainingLanguages];
};

const getLocalizedLabel = (
  names: Record<string, string> | undefined,
  language: SupportedLanguage,
): string => {
  if (!names) {
    return '';
  }

  const languagePriority = buildLanguagePriority(language);

  for (const lang of languagePriority) {
    const rawValue = names[lang];
    if (rawValue) {
      return rawValue;
    }
  }

  const fallbackValue = Object.values(names).find(Boolean);

  return fallbackValue ?? '';
};

const toFormOption = (code: string, label: string): FormOption => ({
  value: code,
  label,
});

const withWahisSuffix = (label: string): string => {
  if (label.trim().length === 0) {
    return label;
  }

  return label.includes('(WAHIS)') ? label : `${label} (WAHIS)`;
};

const buildOptionsFromReferentials = <
  T extends { code: string; name?: Record<string, string> },
>(
  items: T[] | undefined,
  language: SupportedLanguage,
): FormOption[] => {
  if (!Array.isArray(items)) {
    return [];
  }

  return items
    .map((item) =>
      toFormOption(
        item.code,
        getLocalizedLabel(item.name, language) || item.code,
      ),
    )
    .sort((a, b) => a.label.localeCompare(b.label));
};

type EtiologicalAgentWithType = {
  code: string;
  name?: Record<string, string>;
  etiologyTypeCode?: string;
};

const buildOptionsByEtiologyType = <T extends EtiologicalAgentWithType>(
  etiologicalAgents: T[] | undefined,
  language: SupportedLanguage,
): Record<string, FormOption[]> => {
  if (!Array.isArray(etiologicalAgents)) {
    return {};
  }

  const grouped = etiologicalAgents.reduce<Record<string, FormOption[]>>(
    (acc, agent) => {
      if (!agent.etiologyTypeCode) {
        return acc;
      }

      if (!acc[agent.etiologyTypeCode]) {
        acc[agent.etiologyTypeCode] = [];
      }

      acc[agent.etiologyTypeCode].push(
        toFormOption(
          agent.code,
          getLocalizedLabel(agent.name, language) || agent.code,
        ),
      );

      return acc;
    },
    {},
  );

  Object.values(grouped).forEach((options) =>
    options.sort((a, b) => a.label.localeCompare(b.label)),
  );

  return grouped;
};

export const buildSampleTypeOptions = (
  sampleTypes: SampleTypeDto[] | undefined,
  language: SupportedLanguage,
): FormOption[] => buildOptionsFromReferentials(sampleTypes, language);

export const buildSampleTypeOptionsByAnimalType = (
  sampleTypes: SampleTypeDto[] | undefined,
  language: SupportedLanguage,
): Record<string, FormOption[]> => {
  if (!Array.isArray(sampleTypes)) {
    return {};
  }

  const grouped = sampleTypes.reduce<Record<string, FormOption[]>>(
    (acc, sampleType) => {
      const animalType = sampleType.animalType?.toLowerCase().trim();
      if (!animalType) {
        return acc;
      }

      if (!acc[animalType]) {
        acc[animalType] = [];
      }

      acc[animalType].push(
        toFormOption(
          sampleType.code,
          getLocalizedLabel(sampleType.name, language) || sampleType.code,
        ),
      );

      return acc;
    },
    {},
  );

  Object.values(grouped).forEach((options) =>
    options.sort((a, b) => a.label.localeCompare(b.label)),
  );

  return grouped;
};

export const buildSubcategoryOptionsByCategory = (
  subCategories: SubCategoryDto[] | undefined,
  language: SupportedLanguage,
): Record<string, FormOption[]> => {
  if (!Array.isArray(subCategories)) {
    return {};
  }

  const grouped = subCategories.reduce<Record<string, FormOption[]>>(
    (acc, subCategory) => {
      const category = subCategory.categoryOfTest;
      if (!acc[category]) {
        acc[category] = [];
      }

      acc[category].push(
        toFormOption(
          subCategory.code,
          getLocalizedLabel(subCategory.name, language) || subCategory.code,
        ),
      );

      return acc;
    },
    {},
  );

  Object.values(grouped).forEach((options) =>
    options.sort((a, b) => a.label.localeCompare(b.label)),
  );

  return grouped;
};

export const buildFamilyOptionsBySubcategory = (
  families: FamilyDto[] | undefined,
  language: SupportedLanguage,
): Record<string, FormOption[]> => {
  if (!Array.isArray(families)) {
    return {};
  }

  const grouped = families.reduce<Record<string, FormOption[]>>(
    (acc, family) => {
      if (!Array.isArray(family.subCategories)) {
        return acc;
      }

      family.subCategories.forEach((subCategory) => {
        const subcategoryCode = subCategory.code;
        if (!acc[subcategoryCode]) {
          acc[subcategoryCode] = [];
        }

        acc[subcategoryCode].push(
          toFormOption(
            family.code,
            getLocalizedLabel(family.name, language) || family.code,
          ),
        );
      });

      return acc;
    },
    {},
  );

  Object.values(grouped).forEach((options) =>
    options.sort((a, b) => a.label.localeCompare(b.label)),
  );

  return grouped;
};

export const buildEtiologyTypeOptions = (
  etiologyTypes: EtiologyTypeDto[] | undefined,
  language: SupportedLanguage,
): FormOption[] => buildOptionsFromReferentials(etiologyTypes, language);

export const buildEtiologicalAgentOptions = (
  etiologicalAgents: EtiologicalAgentDto[] | undefined,
  language: SupportedLanguage,
): FormOption[] => buildOptionsFromReferentials(etiologicalAgents, language);

export const buildEtiologicalAgentOptionsByEtiologyType = (
  etiologicalAgents: EtiologicalAgentDto[] | undefined,
  language: SupportedLanguage,
): Record<string, FormOption[]> =>
  buildOptionsByEtiologyType(etiologicalAgents, language);

export const buildWahisEtiologicalAgentOptions = (
  etiologicalAgents: WahisEtiologicalAgentDto[] | undefined,
  language: SupportedLanguage,
): FormOption[] =>
  buildOptionsFromReferentials(etiologicalAgents, language).map((option) => ({
    ...option,
    label: withWahisSuffix(option.label),
  }));

export const buildWahisEtiologicalAgentOptionsByEtiologyType = (
  etiologicalAgents: WahisEtiologicalAgentDto[] | undefined,
  language: SupportedLanguage,
): Record<string, FormOption[]> =>
  Object.fromEntries(
    Object.entries(buildOptionsByEtiologyType(etiologicalAgents, language)).map(
      ([key, options]) => [
        key,
        options.map((option) => ({
          ...option,
          label: withWahisSuffix(option.label),
        })),
      ],
    ),
  );
